import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { useEffect, useState } from 'react';
import { showMessage } from '../../../Services/ToastService';
import { IRide, IRidePosition } from '../../RidePlayer/types';
import LoaderService from '../../../Services/LoaderService';
import {
  isOnlinePanelOpenAtom,
  IVehicleOnlinePanels,
  onlinePanelAtom,
} from '../../OnlinePanel/OnlinePanelState';
import usePrevious from '../../../hooks/usePrevious';
import {
  ILastRideDistanceCumulative,
  IVehicleWithData,
  TVehicleRidePositionType,
} from '../../Vehicles/types';
import { getVehicleLastRide } from '../../RidePlayer/RidePlayerService';
import {
  activeLastRideIdAtom,
  ignoreRideChangeAtom,
  reloadLastRideAtom,
  showChangeRideConfirmDialogAtom,
  showLastRideChangedWarningAtom,
} from '../../RidePlayer/RidePlayerState';
import {
  autofocusAtom,
  currentRidePositionForSelectedVehicleAtom,
  isRidePlayerOpenAtom,
  lastRideForSelectedVehicleAtom,
  vehicleFocusAtom,
} from '../OnlineMapState';
import {
  lastRideDistanceCumulativeAtom,
  selectedVehiclesAtom,
  vehicleWithDataSelector,
} from '../../../store/recoil/vehicles';

interface IUseOnlineMap {
  isLoadingRide: boolean;
  loadingRideError: boolean;
}

/**
 * useOnlineMap custom hook
 * @returns {IUseOnlineMap}
 */
const useOnlineMap = (googleMap: google.maps.Map | undefined): IUseOnlineMap => {
  const { formatMessage: f } = useIntl();
  const onlinePanelData = useRecoilValue<IVehicleOnlinePanels>(onlinePanelAtom);
  const isOnlinePanelOpen = useRecoilValue<number | false>(isOnlinePanelOpenAtom);
  const vehicleWithData = useRecoilValue<IVehicleWithData | null>(
    vehicleWithDataSelector(isOnlinePanelOpen)
  );
  const setCurrentRidePositionForSelectedVehicle = useSetRecoilState<IRidePosition | null>(
    currentRidePositionForSelectedVehicleAtom
  );
  const setLastRideForSelectedVehicle = useSetRecoilState<IRide | null>(
    lastRideForSelectedVehicleAtom
  );
  const [isRidePlayerOpen, setIsRidePlayerOpen] = useRecoilState<number | boolean>(
    isRidePlayerOpenAtom
  );
  const setActiveLastRideId = useSetRecoilState<string | null>(activeLastRideIdAtom);
  const [reloadLastRide, setReloadLastRide] = useRecoilState<boolean>(reloadLastRideAtom);
  const [isLoadingRide, setIsLoadingRide] = useState<boolean>(false);
  const [loadingRideError, setLoadingRideError] = useState<boolean>(false);
  const setIsOnlinePanelOpen = useSetRecoilState<number | false>(isOnlinePanelOpenAtom);
  const [lastRideDistanceCumulative, setLastRideDistanceCumulative] =
    useRecoilState<ILastRideDistanceCumulative | null>(lastRideDistanceCumulativeAtom);
  const prevIsRidePlayerOpen = usePrevious(isRidePlayerOpen, { jsonParse: false });
  const prevVehicleWithData: IVehicleWithData = usePrevious(vehicleWithData, { jsonParse: false });
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const setShowLastRideChangedWarning = useSetRecoilState<boolean>(showLastRideChangedWarningAtom);
  const [ignoreRideChange, setIgnoreRideChange] = useRecoilState<boolean>(ignoreRideChangeAtom);
  const setAutofocus = useSetRecoilState<boolean>(autofocusAtom);
  const setVehicleFocus = useSetRecoilState<boolean>(vehicleFocusAtom);
  const showChangeRideConfirmDialog = useRecoilValue<boolean>(showChangeRideConfirmDialogAtom);

  // Initialize google map
  useEffect(() => {
    return () => {
      setLastRideForSelectedVehicle(null);
      setCurrentRidePositionForSelectedVehicle(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedVehicles.includes(Number(isOnlinePanelOpen))) {
      setIsOnlinePanelOpen(false);
      setAutofocus(true);
      setVehicleFocus(false);
    }
  }, [isOnlinePanelOpen, selectedVehicles, setAutofocus, setIsOnlinePanelOpen, setVehicleFocus]);

  // When last ride player is open, hide streetview control on map
  // React.useEffect(() => {
  //   if (isRidePlayerOpen) {
  //     googleMap?.setOptions({ streetViewControl: false });
  //   } else {
  //     googleMap?.setOptions({ streetViewControl: true });
  //   }
  // }, [isRidePlayerOpen, googleMap]);

  // When ride player has been closed, reset all his states
  useEffect(() => {
    if (prevIsRidePlayerOpen && isRidePlayerOpen === false) {
      setLastRideForSelectedVehicle(null);
      setActiveLastRideId(null);
    }
  }, [isRidePlayerOpen, prevIsRidePlayerOpen, setActiveLastRideId, setLastRideForSelectedVehicle]);

  const handleLastRideDistanceCumulative = (vehicleId: number, positions: IRidePosition[]) => {
    const lastPosition: IRidePosition = Object.values(positions)[Object.keys(positions).length - 1];
    setLastRideDistanceCumulative({
      ...lastRideDistanceCumulative,
      [vehicleId]: lastPosition.distanceCumulative,
    });
  };

  // Reload changed last ride
  useEffect(() => {
    if (reloadLastRide) {
      const loadLastRide = async () => {
        if (vehicleWithData && vehicleWithData.lastRide) {
          const isLastRidePlayer = true;
          setIsLoadingRide(true);
          setIsRidePlayerOpen(false);
          setShowLastRideChangedWarning(false);
          LoaderService.showLoader();
          const ride = await getVehicleLastRide(vehicleWithData.id);
          if (ride && ride[0]) {
            const vehicleRide: IRide = {
              positions: ride[0].positions,
              rideData: ride[0].rideData,
            };
            setLastRideForSelectedVehicle(vehicleRide);
            setIsLoadingRide(false);
            setLoadingRideError(false);
            setReloadLastRide(false);
            setIgnoreRideChange(false);
            setActiveLastRideId(String(0));
            setIsOnlinePanelOpen(vehicleWithData.id);
            setIsRidePlayerOpen(vehicleWithData.id);
            if (isLastRidePlayer) {
              handleLastRideDistanceCumulative(vehicleWithData.id, ride[0].positions);
            }
            LoaderService.showLoader(false);
          } else {
            showMessage(
              f({ id: 'toast.ridePlayer.cannotLoadRide.title' }),
              f({ id: 'toast.ridePlayer.cannotLoadRide.message' }),
              'error'
            );
            setLoadingRideError(true);
            setIsLoadingRide(false);
            setReloadLastRide(false);
            setActiveLastRideId(null);
            LoaderService.showLoader(false);
          }
        }
        setIsLoadingRide(false);
        LoaderService.showLoader(false);
      };
      loadLastRide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadLastRide, setReloadLastRide]);

  // Add new position to ride player
  useEffect(() => {
    if (
      vehicleWithData &&
      vehicleWithData.position &&
      vehicleWithData.onlinePanel &&
      vehicleWithData.id === isRidePlayerOpen &&
      vehicleWithData.position.latitude !== prevVehicleWithData?.position?.latitude &&
      vehicleWithData.position.longitude !== prevVehicleWithData?.position?.longitude &&
      !ignoreRideChange &&
      !showChangeRideConfirmDialog
      // activeLastRideId === '0'
      // vehicleWithData.position.lastRideId === activeLastRideId &&
      // vehicleWithData.position.parkingId === null
    ) {
      const onlinePanel = isOnlinePanelOpen ? onlinePanelData[isOnlinePanelOpen] : null;
      const vehicleRidePosition: IRidePosition = {
        announcements: onlinePanel?.announcements || [],
        distance: 0,
        distanceCumulative: vehicleWithData.position.distanceCumulative || 0,
        gpsAlt: vehicleWithData.position.altitude,
        gpsCourse: vehicleWithData.position.angle,
        address: vehicleWithData.position.address || null,
        gpsLat: vehicleWithData.position.latitude,
        gpsLon: vehicleWithData.position.longitude,
        speed: onlinePanel?.speed || 0,
        timestamp: onlinePanel?.timestamp || '',
        type: vehicleWithData.status as TVehicleRidePositionType,
        ecoScore: onlinePanel?.ecoDriveScore || null,
        // id: vehicleWithData.position.positionId,
        id: uuidv4(),
      };
      setCurrentRidePositionForSelectedVehicle(vehicleRidePosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnlinePanelOpen, vehicleWithData, vehicleWithData?.position]);

  const data: IUseOnlineMap = {
    isLoadingRide,
    loadingRideError,
  };

  return data;
};

export default useOnlineMap;
